<template>
  <form @submit.prevent="submit">
    <p class="subtitle underlined">Opret studerende</p>

    <div class="columns">
      <div class="column">
        <b-field
          :label="$t('global.forms.name')"
          :type="{ 'is-danger': veeErrors.has('name') }"
          :message="veeErrors.first('name')"
        >
          <b-input
            v-model="name"
            name="name"
            v-validate="'required'"
            :data-vv-as="$t('global.forms.name')"
          />
        </b-field>

        <b-field
          :label="$t('global.forms.email')"
          :type="{ 'is-danger': veeErrors.has('email') }"
          :message="veeErrors.first('email')"
        >
          <b-input
            v-model="email"
            type="email"
            name="email"
            v-validate="'required|email'"
            :data-vv-as="$t('global.forms.email')"
          />
        </b-field>
      </div>

      <div class="column">
        <b-field
          :label="$t('global.forms.phoneNumber')"
          :type="{ 'is-danger': veeErrors.has('phoneNumber') }"
          :message="veeErrors.first('phoneNumber')"
        >
          <b-input
            v-model="phone_number"
            type="phone"
            name="phoneNumber"
            v-validate="'required|min:8'"
            :data-vv-as="$t('global.forms.phoneNumber')"
          />
        </b-field>

        <!-- Date of birth -->
        <b-field
          :label="$t('global.forms.dateOfBirth')"
          :type="{ 'is-danger': veeErrors.has('date_of_birth') }"
          :message="veeErrors.first('date_of_birth')"
        >
          <b-datepicker
            v-model="date_of_birth"
            :show-week-number="false"
            placeholder="Klik for at vælge"
            icon="calendar-today"
            trap-focus
          >
          </b-datepicker>
        </b-field>
      </div>
    </div>

    <br />

    <p class="subtitle underlined">Vælg færdigheder og niveau</p>

    <div class="columns">
      <div class="column">
        <b-field label="Færdigheder">
          <b-select
            v-model="selectedSkills"
            expanded
            :loading="!skillGroups.length"
            multiple
          >
            <optgroup
              v-for="skillGroup in skillGroups"
              :key="skillGroup.id"
              :label="$t('skills.groups.' + skillGroup.name + '.name')"
            >
              <option
                v-for="skill in skillGroup.skills"
                :value="skill"
                :key="skill.id"
              >
                {{ $t('skills.' + skill.name + '.name') }}
              </option>
            </optgroup>
          </b-select>
        </b-field>

        <p>Vælg flere ved at holde Control eller Command nede.</p>
        <br />
        <b-notification
          v-if="selectedSkills.length === 0"
          :active="true"
          type="is-danger"
          :closable="false"
        >
          Der skal vælges mindst én færdighed
        </b-notification>

        <p v-else>
          Valgte færdigheder:
          <b-tag
            v-for="skill in selectedSkills"
            :key="skill.id"
            type="is-primary"
            style="margin-right: 5px"
          >
            {{ $t('skills.' + skill.name + '.name') }}
          </b-tag>
        </p>
      </div>

      <div class="column">
        <b-field
          label="Niveau"
          :type="{ 'is-danger': veeErrors.has('level') }"
          :message="veeErrors.first('level')"
        >
          <b-select
            v-model="level_id"
            expanded
            :loading="!levels.length"
            name="level"
            v-validate="'required'"
            data-vv-as="Niveau"
          >
            <option :value="null">Vælg et niveau</option>
            <option v-for="level in levels" :value="level.id" :key="level.id">
              {{ $t(`levels.${level.name}.name`) }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>

    <br />

    <p class="subtitle underlined">Særlige ting</p>

    <div class="columns">
      <div class="column">
        <b-field label="Opret samtaler med ansatte">
          <b-select
            v-model="employeeConversations"
            expanded
            :loading="!employees.length"
            multiple
          >
            <option
              v-for="employee in employees"
              :key="employee.id"
              :value="employee"
            >
              {{ employee.name }}
            </option>
          </b-select>
        </b-field>

        <p>Vælg flere ved at holde Control eller Command nede.</p>
        <br />

        <p v-if="employeeConversations.length">
          Valgte ansatte:
          <b-tag
            v-for="employee in employeeConversations"
            :key="employee.id"
            type="is-primary"
            style="margin-right: 5px"
          >
            {{ employee.name }}
          </b-tag>
        </p>
      </div>

      <div class="column">
        <b-field>
          <b-checkbox v-model="activateUser">
            Aktiver den studerendes bruger med det samme.
          </b-checkbox>
        </b-field>

        <b-field>
          <b-checkbox v-model="useGeneratedPassword">
            Brug et autogeneret kodeord. Det sendes til den angivne email.
          </b-checkbox>
        </b-field>

        <b-field
          v-if="!useGeneratedPassword"
          :label="$t('global.forms.password')"
          :type="{ 'is-danger': veeErrors.has('password') }"
          :message="veeErrors.first('password')"
        >
          <b-input
            v-model="password"
            type="password"
            name="password"
            v-validate="'required'"
            :data-vv-as="$t('global.forms.password')"
            password-reveal
          />
        </b-field>

        <b-field>
          <b-checkbox
            v-model="skipEmailVerification"
            :disabled="useGeneratedPassword"
          >
            Send velkomst mail med email-godkendelse.
          </b-checkbox>
        </b-field>
        <span class="help" v-if="useGeneratedPassword"
          >Vi er nødt til at sende velkomstemailen ved brug af det autogenerede
          kodeord</span
        >
      </div>
    </div>

    <b-notification
      v-if="success"
      :active.sync="success"
      type="is-success"
      :closable="false"
    >
      <p>
        Den studerende er blevet oprettet. Opdater siden for at se dem i
        tabellen.
      </p>
    </b-notification>

    <b-notification v-else-if="error" :active.sync="error" type="is-danger">
      Noget gik galt, prøv igen.
    </b-notification>

    <br />

    <div class="buttons is-right">
      <button class="button" @click="$emit('hide')" :disabled="isSubmitting">
        <span>{{ $t('global.actions.close') }}</span>
      </button>
      <button
        v-if="!success"
        type="submit"
        class="button is-primary"
        :class="{ 'is-loading': isSubmitting }"
        :disabled="veeErrors.any()"
      >
        <span>{{ $t('global.actions.register') }}</span>
      </button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import '../../../../../node_modules/flatpickr/dist/flatpickr.css';
</style>

<script>
import Datepicker from 'vue-flatpickr-component'
import { Danish } from 'flatpickr/dist/l10n/da'
import StudentService from '@/taskflex/students/services/StudentService'
import LevelService from '@/taskflex/levels/services/LevelService'
import SkillGroupService from '@/taskflex/skills/services/SkillGroupService'
import EmployeeService from '@/taskflex/employees/services/EmployeeService'

const moment = require('moment')

export default {
  data () {
    return {
      error: false,
      success: false,
      skillGroups: [],
      levels: [],
      employees: [],
      employeeConversations: [],

      selectedSkills: [],
      level_id: null,
      email: null,
      name: null,
      phone_number: null,
      date_of_birth: null,
      password: null,

      isSubmitting: false,
      useGeneratedPassword: true,
      activateUser: false,
      skipEmailVerification: true
    }
  },

  computed: {
    datepickerConfig () {
      return {
        altFormat: 'j. F Y',
        altInput: true,
        maxDate: moment()
          .subtract(17, 'years')
          .format('Y-MM-DD'),
        locale: Danish
      }
    }
  },

  watch: {
    useGeneratedPassword (value) {
      if (value) {
        this.skipEmailVerification = true
        this.password = null
      }
    }
  },

  mounted () {
    this.loadSkillGroups()
    this.loadLevels()
    this.loadEmployees()
  },

  methods: {
    async submit () {
      if (!(await this.$validator.validateAll())) {
        this.isSubmitting = false
        return
      }

      this.isSubmitting = true

      const data = {
        name: this.name,
        email: this.email,
        phone_number: this.phone_number,
        date_of_birth: this.date_of_birth.toISOString().split('T')[0],
        level_id: this.level_id,
        skills: this.selectedSkills.map(skill => skill.id),
        skipEmailVerification: !this.skipEmailVerification,
        activateUser: this.activateUser,
        employeeConversations: this.employeeConversations.map(
          employee => employee.id
        )
      }

      if (!this.useGeneratedPassword) {
        data.password = this.password
      }

      StudentService.create(
        data,
        () => {
          this.isSubmitting = false
          this.success = true
        },
        ({ response }) => {
          const { errors } = response.data

          this.isSubmitting = false

          if (!errors) {
            // eslint-disable-next-line no-console
            console.log(response)
            this.error = true
            return
          }

          Object.entries(errors).forEach(([key, values]) => {
            values.forEach(value => {
              if (value === 'validation.unique') {
                this.veeErrors.add({
                  field: key,
                  msg: 'Denne email er allerede registeret i vores system.'
                })
              }
            })
          })
        }
      )
    },

    loadSkillGroups () {
      const options = {
        includes: ['skills'],
        distinct: true,
        filter_groups: [
          {
            filters: [
              {
                key: 'enabled',
                value: 1,
                operator: 'eq'
              },
              {
                key: 'skillsEnabled',
                value: 1,
                operator: 'eq'
              }
            ]
          }
        ]
      }

      SkillGroupService.findAll(options, response => {
        const { rows } = response.data
        this.skillGroups = rows
      })
    },

    loadLevels () {
      const options = {
        sort: [
          {
            key: 'order',
            direction: 'asc'
          }
        ],

        filter_groups: [
          {
            filters: [
              {
                key: 'enabled',
                operator: 'eq',
                value: 1
              }
            ]
          }
        ]
      }

      LevelService.findAll(options, response => {
        const { rows } = response.data

        this.levels = rows
      })
    },

    loadEmployees () {
      const options = {}

      EmployeeService.findAll(options, response => {
        const { rows } = response.data

        this.employees = rows
      })
    }
  },

  components: {
    Datepicker
  }
}
</script>



// WEBPACK FOOTER //
// src/taskflex/administration/components/students/RegistrationModal.vue